import { CreatePaymentSchema, PaymentSchema, UpdatePaymentSchema, ZodGetPaymentParams } from "@billingnow/zod-schema";
import { PaginationMeta, PaginationParams } from "./pagination";
import { apiClient } from "./client";

export type Payment = PaymentSchema;
export type PaymentWithEntities = PaymentSchema & {
  invoice: { invoiceNumber: string; currency: string } | null;
  creditNote: { creditNoteNumber: string; currency: string } | null;
  expense: { expenseNumber: string; currency: string } | null;
};

export const createPayment = async (paymentData: CreatePaymentSchema): Promise<{ payment: Payment }> => {
  const response = await apiClient.post<{ payment: Payment }>("/api/payment", paymentData, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data;
};

/**
 * Get payments - returns all payments (for backward compatibility)
 * @deprecated Use getPaymentsPaginated for better performance with large datasets
 */
export const getPayments = async (params?: ZodGetPaymentParams): Promise<PaymentWithEntities[]> => {
  const response = await apiClient.get<{ payments: PaymentWithEntities[]; pagination: PaginationMeta }>("/api/payment", {
    params,
  });
  return response.data.payments;
};

/**
 * Paginated response for payments
 */
export interface GetPaymentsPaginatedResponse {
  payments: PaymentWithEntities[];
  pagination: PaginationMeta;
}

/**
 * Get payments with pagination support
 */
export const getPaymentsPaginated = async (
  params?: ZodGetPaymentParams & PaginationParams,
): Promise<GetPaymentsPaginatedResponse> => {
  const response = await apiClient.get<GetPaymentsPaginatedResponse>("/api/payment", { params });
  return response.data;
};

export const getPaymentsByEntity = async (entityId: string): Promise<{ payments: Payment[] }> => {
  const response = await apiClient.get<{ payments: Payment[] }>(`/api/payment/${entityId}`);
  return response.data;
};

export const updatePayment = async (
  paymentId: string,
  paymentData: UpdatePaymentSchema,
): Promise<{ payment: Payment }> => {
  const response = await apiClient.put<{ payment: Payment }>(`/api/payment/${paymentId}`, paymentData, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data;
};

export const deletePayment = async (paymentId: string): Promise<{ message: string; payment: Payment }> => {
  const response = await apiClient.delete<{ message: string; payment: Payment }>(`/api/payment/${paymentId}`);
  return response.data;
};

/**
 * Fetch all payments by iterating through all pages
 * Used for export functionality to get complete dataset
 */
export const getAllPayments = async (params?: ZodGetPaymentParams): Promise<PaymentWithEntities[]> => {
  const allPayments: PaymentWithEntities[] = [];
  let cursor: string | null = null;

  do {
    const response = await getPaymentsPaginated({ ...params, cursor, limit: 100 });
    allPayments.push(...response.payments);
    cursor = response.pagination.nextCursor;
  } while (cursor);

  return allPayments;
};
